import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

type LanguageContextType = {
  language: string;
  changeLanguage: (lang: string) => Promise<void>;
  t: (key: string, options?: Record<string, unknown>) => string;
  isRTL: boolean;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize i18n on mount
  useEffect(() => {
    const init = async () => {
      try {
        await i18n.init();
        setCurrentLanguage(i18n.language);
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize i18n:', error);
      }
    };

    if (!i18n.isInitialized) {
      init();
    } else {
      setCurrentLanguage(i18n.language);
      setIsInitialized(true);
    }
  }, []);

  const changeLanguage = async (lang: string) => {
    if (lang === i18n.language) return;
    
    try {
      await i18n.changeLanguage(lang);
      // The languageChanged event will update the state
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  // Handle language changes
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      if (lng !== currentLanguage) {
        setCurrentLanguage(lng);
        document.documentElement.lang = lng;
        document.documentElement.dir = i18n.dir(lng);
        localStorage.setItem('i18nextLng', lng);
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [currentLanguage, i18n]);

  // Only render children when i18n is ready
  if (!isInitialized) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading language resources...</div>
      </div>
    );
  }

  return (
    <LanguageContext.Provider
      value={{
        language: currentLanguage,
        changeLanguage,
        t,
        isRTL: i18n.dir() === 'rtl',
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};